/* eslint-disable react/react-in-jsx-scope */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronDown, ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { useMemo, useState } from "react";

type IntroStatTone =
  | "sand"
  | "olive"
  | "teal"
  | "blue"
  | "violet"
  | "rose"
  | "slate";

export type IntroStat = {
  key: string;
  label: string;
  value: number | string;
  valueDisplay?: string;
  suffix?: string;
  meta?: string;
  tone?: IntroStatTone;
  hideDots?: boolean;
};

export type CalendarEventInput =
  | string
  | Date
  | {
      date: string | Date;
      count?: number;
      label?: string;
      targetId?: string;
      projectId?: number;
      funderId?: number;
    };

export type CalendarDetailItem = {
  label: string;
  count: number;
  targetId?: string;
  projectId?: number;
  funderId?: number;
};

export type FundingReminderSection = "today" | "upcoming" | "overdue";

export type FundingReminderItem = {
  id: string;
  label: string;
  date: string | Date;
  section: FundingReminderSection;
  targetId?: string;
  projectId?: number;
  funderId?: number;
};

function normalizeEventTypeLabel(label: string) {
  const raw = String(label || "").trim();
  if (!raw) return "élément";
  const parts = raw.split("·").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) return parts[parts.length - 1];
  return raw;
}

function normalizeEventContextLabel(label: string) {
  const raw = String(label || "").trim();
  if (!raw) return "Élément";
  const parts = raw.split("·").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) return parts.slice(0, -1).join(" · ");
  return raw;
}

function getCalendarEventState(label: string) {
  const typeLabel = normalizeEventTypeLabel(label).toLowerCase();
  if (
    typeLabel.includes("prévu") ||
    typeLabel.includes("prevu") ||
    typeLabel.includes("à faire") ||
    typeLabel.includes("a faire") ||
    typeLabel.includes("à effectuer") ||
    typeLabel.includes("a effectuer") ||
    typeLabel.includes("en attente") ||
    typeLabel.includes("retard")
  ) {
    return "action";
  }
  return "done";
}

function getCalendarEventStateStyles(state: "action" | "done") {
  if (state === "action") {
    return {
      dot: "bg-red-500",
      text: "text-red-600",
      badge: "bg-red-50 text-red-700",
    };
  }
  return {
    dot: "bg-emerald-500",
    text: "text-emerald-600",
    badge: "bg-emerald-50 text-emerald-700",
  };
}

function getToneStyles(tone: IntroStatTone) {
  switch (tone) {
    case "sand":
      return {
        card: "border-[#ff0000]/25 bg-[#ff0000]/8",
        label: "text-[#c81e1e]",
        value: "text-[#a51616]",
        meta: "text-[#b84c4c]",
        dots: ["bg-[#ff0000]"],
        activeRing: "ring-2 ring-[#ff0000]/35",
        hoverRing: "hover:ring-1 hover:ring-[#ff0000]/20",
      };
    case "olive":
      return {
        card: "border-verdeogreen/25 bg-verdeogreen/10",
        label: "text-verdeogreen",
        value: "text-verdeogreen",
        meta: "text-verdeogreen/80",
        dots: ["bg-verdeogreen"],
        activeRing: "ring-2 ring-verdeogreen/30",
        hoverRing: "hover:ring-1 hover:ring-verdeogreen/20",
      };
    case "teal":
      return {
        card: "border-[#a1560b]/25 bg-[#a1560b]/10",
        label: "text-[#8b4a09]",
        value: "text-[#7a4108]",
        meta: "text-[#8b4a09]/80",
        dots: ["bg-[#a1560b]"],
        activeRing: "ring-2 ring-[#a1560b]/30",
        hoverRing: "hover:ring-1 hover:ring-[#a1560b]/20",
      };
    case "blue":
      return {
        card: "border-verdeoblue/25 bg-verdeoblue/10",
        label: "text-verdeoblue",
        value: "text-verdeoblue",
        meta: "text-verdeoblue/80",
        dots: ["bg-verdeoblue"],
        activeRing: "ring-2 ring-verdeoblue/30",
        hoverRing: "hover:ring-1 hover:ring-verdeoblue/20",
      };
    case "violet":
      return {
        card: "border-[#425e1a]/25 bg-[#425e1a]/10",
        label: "text-[#425e1a]",
        value: "text-[#344816]",
        meta: "text-[#425e1a]/80",
        dots: ["bg-[#425e1a]"],
        activeRing: "ring-2 ring-[#425e1a]/30",
        hoverRing: "hover:ring-1 hover:ring-[#425e1a]/20",
      };
    case "rose":
      return {
        card: "border-verdeoblue/25 bg-verdeoblue/8",
        label: "text-slate-800",
        value: "text-slate-900",
        meta: "text-slate-600",
        dots: ["bg-verdeoblue", "bg-verdeogreen", "bg-verdeoorange"],
        activeRing: "ring-2 ring-verdeoblue/30",
        hoverRing: "hover:ring-1 hover:ring-verdeoblue/20",
      };
    case "slate":
    default:
      return {
        card: "border-slate-200 bg-white",
        label: "text-slate-800",
        value: "text-slate-900",
        meta: "text-slate-500",
        dots: ["bg-slate-400"],
        activeRing: "ring-2 ring-slate-700/60",
        hoverRing: "hover:ring-1 hover:ring-slate-300",
      };
  }
}

function toDateKey(input: string | Date) {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function normalizeCalendarEvents(events: CalendarEventInput[]) {
  const map = new Map<
    string,
    { total: number; details: CalendarDetailItem[] }
  >();
  for (const event of events) {
    const input = typeof event === "object" && "date" in event ? event.date : event;
    const eventCount =
      typeof event === "object" && "count" in event ? Number(event.count ?? 1) : 1;
    const eventLabel =
      typeof event === "object" && "label" in event
        ? String(event.label || "").trim()
        : "";
    const key = toDateKey(input);
    if (!key) continue;
    const count = Number.isFinite(eventCount) && eventCount > 0 ? eventCount : 1;
    const item = map.get(key) || { total: 0, details: [] };
    const detail: CalendarDetailItem = {
      label: eventLabel || "Événement",
      count,
      ...(typeof event === "object" && "targetId" in event
        ? { targetId: event.targetId }
        : {}),
      ...(typeof event === "object" && "projectId" in event
        ? { projectId: event.projectId }
        : {}),
      ...(typeof event === "object" && "funderId" in event
        ? { funderId: event.funderId }
        : {}),
    };
    item.total += count;
    item.details.push(detail);
    map.set(key, item);
  }
  return map;
}

function buildCalendarCells(cursor: Date) {
  const firstDay = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const daysInMonth = new Date(
    cursor.getFullYear(),
    cursor.getMonth() + 1,
    0,
  ).getDate();
  const firstWeekDayMonday0 = (firstDay.getDay() + 6) % 7;
  const totalCells = Math.ceil((firstWeekDayMonday0 + daysInMonth) / 7) * 7;

  const cells: Array<{ date: Date | null; day: number | null }> = [];
  for (let i = 0; i < totalCells; i++) {
    const day = i - firstWeekDayMonday0 + 1;
    if (day < 1 || day > daysInMonth) {
      cells.push({ date: null, day: null });
      continue;
    }
    cells.push({
      date: new Date(cursor.getFullYear(), cursor.getMonth(), day),
      day,
    });
  }
  return cells;
}

function parseDateOrNull(value: string | Date) {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getReminderSectionTone(section: FundingReminderSection) {
  switch (section) {
    case "overdue":
      return {
        badge: "bg-rose-100 text-rose-700",
        border: "border-rose-200",
      };
    case "today":
      return {
        badge: "bg-amber-100 text-amber-700",
        border: "border-amber-200",
      };
    case "upcoming":
    default:
      return {
        badge: "bg-blue-100 text-blue-700",
        border: "border-blue-200",
      };
  }
}

function getReminderSectionLabel(section: FundingReminderSection) {
  switch (section) {
    case "overdue":
      return "En retard";
    case "today":
      return "Aujourd'hui";
    case "upcoming":
    default:
      return "7 prochains jours";
  }
}

function formatReminderDate(input: string | Date) {
  const date = parseDateOrNull(input);
  if (!date) return "Date inconnue";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function ReminderPanel({
  reminderItems,
  onReminderClick,
}: {
  reminderItems: FundingReminderItem[];
  onReminderClick?: (item: FundingReminderItem) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const grouped = useMemo(() => {
    const initial: Record<FundingReminderSection, FundingReminderItem[]> = {
      overdue: [],
      today: [],
      upcoming: [],
    };

    for (const item of reminderItems || []) {
      if (!item?.section || !initial[item.section]) continue;
      initial[item.section].push(item);
    }

    for (const section of Object.keys(initial) as FundingReminderSection[]) {
      initial[section].sort((a, b) => {
        const da = parseDateOrNull(a.date)?.getTime() ?? 0;
        const db = parseDateOrNull(b.date)?.getTime() ?? 0;
        return da - db;
      });
    }

    return initial;
  }, [reminderItems]);

  const total = reminderItems.length;
  const sections: FundingReminderSection[] = ["overdue", "today", "upcoming"];

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4">
      <button
        type="button"
        className={[
          "flex w-full items-center justify-between rounded-lg py-1 text-left hover:bg-slate-50",
          isOpen ? "mb-3" : "mb-0",
        ].join(" ")}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className="min-w-0 whitespace-nowrap text-sm font-semibold text-slate-900">
          Rappels automatiques
        </div>
        <div className="ml-2 flex shrink-0 items-center gap-2">
          <div
            className={[
              "inline-flex h-6 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold text-white",
              total > 9 ? "min-w-6 px-1.5" : "w-6",
            ].join(" ")}
          >
            {total > 99 ? "99+" : total}
          </div>
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-slate-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-500" />
          )}
        </div>
      </button>

      {isOpen ? (
        <div className="space-y-2">
          {sections.map((section) => {
            const tone = getReminderSectionTone(section);
            const items = grouped[section] || [];
            const visibleItems = items.slice(0, 5);
            const remaining = Math.max(0, items.length - visibleItems.length);
            return (
              <div
                key={section}
                className={`rounded-xl border p-2 ${tone.border}`}
              >
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${tone.badge}`}
                  >
                    {getReminderSectionLabel(section)}
                  </span>
                  <span className="text-[11px] font-medium text-slate-600">
                    {items.length}
                  </span>
                </div>

                {visibleItems.length === 0 ? (
                  <div className="px-1 py-1 text-[11px] text-slate-400">
                    Aucun rappel
                  </div>
                ) : (
                  <div className="max-h-44 space-y-1 overflow-y-auto pr-1">
                    {visibleItems.map((item) => {
                      const typeLabel = normalizeEventTypeLabel(item.label);
                      const contextLabel = normalizeEventContextLabel(item.label);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          className="w-full rounded-md px-2 py-1 text-left hover:bg-slate-100"
                          onClick={() => onReminderClick?.(item)}
                        >
                          <div className="truncate text-[11px] font-medium text-slate-800">
                            {contextLabel}
                          </div>
                          <div className="truncate text-[10px] text-slate-500">
                            {typeLabel} · {formatReminderDate(item.date)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {remaining > 0 ? (
                  <div className="mt-1 px-1 text-[10px] text-slate-400">
                    +{remaining} autre(s)
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function MiniCalendar({
  events,
  onCalendarEventClick,
}: {
  events: CalendarEventInput[];
  onCalendarEventClick?: (item: CalendarDetailItem) => void;
}) {
  const [cursor, setCursor] = useState(() => {
    const first = events.find((item) => {
      const raw = typeof item === "object" && "date" in item ? item.date : item;
      return !!toDateKey(raw);
    });
    const firstDateRaw =
      typeof first === "object" && first && "date" in first ? first.date : first;
    const firstDate = firstDateRaw ? new Date(firstDateRaw) : new Date();
    if (Number.isNaN(firstDate.getTime())) return new Date();
    return new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
  });

  const eventMap = useMemo(() => normalizeCalendarEvents(events), [events]);
  const cells = useMemo(() => buildCalendarCells(cursor), [cursor]);
  const label = useMemo(() => {
    const raw = new Intl.DateTimeFormat("fr-FR", {
      month: "long",
      year: "numeric",
    }).format(cursor);
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }, [cursor]);

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4">
      <div className="mb-4 flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          className="h-8 w-8 p-0"
          onClick={() =>
            setCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
          }
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        <Button
          type="button"
          variant="ghost"
          className="h-8 w-8 p-0"
          onClick={() =>
            setCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
          }
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-500">
        {["lu", "ma", "me", "je", "ve", "sa", "di"].map((w) => (
          <div key={w} className="py-1 font-medium">
            {w}
          </div>
        ))}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((cell, index) => {
          if (!cell.date || !cell.day) {
            return <div key={`empty-${index}`} className="h-9 rounded-md" />;
          }
          const key = toDateKey(cell.date);
          const item = key ? eventMap.get(key) : undefined;
          const count = item?.total || 0;
          const details = item
            ? [...item.details]
                .sort((a, b) => a.label.localeCompare(b.label, "fr"))
                .reduce<CalendarDetailItem[]>((acc, current) => {
                  const match = acc.find(
                    (x) =>
                      x.label === current.label &&
                      x.targetId === current.targetId &&
                      x.projectId === current.projectId &&
                      x.funderId === current.funderId,
                  );
                  if (match) {
                    match.count += current.count;
                    return acc;
                  }
                  acc.push({ ...current });
                  return acc;
                }, [])
            : [];
          const typeSummary = details
            .reduce<Record<string, number>>((acc, detail) => {
              const typeLabel = normalizeEventTypeLabel(detail.label);
              acc[typeLabel] = (acc[typeLabel] || 0) + detail.count;
              return acc;
            }, {});
          const actionableCount = details.reduce((sum, detail) => {
            return getCalendarEventState(detail.label) === "action"
              ? sum + detail.count
              : sum;
          }, 0);
          const completedCount = Math.max(0, count - actionableCount);
          const hasActionable = actionableCount > 0;
          const hasCompleted = completedCount > 0;
          const countTone = hasActionable
            ? getCalendarEventStateStyles("action")
            : getCalendarEventStateStyles("done");
          const typeSummaryLabel = Object.entries(typeSummary)
            .sort((a, b) => a[0].localeCompare(b[0], "fr"))
            .map(([typeLabel, qty]) => `${typeLabel} x${qty}`)
            .join(" · ");
          return (
            <div
              key={key || `day-${index}`}
              className="group relative flex h-9 flex-col items-center justify-center rounded-md"
            >
              <span className="text-xs text-slate-700">{cell.day}</span>
              {count > 0 ? (
                <span
                  className={`mt-0.5 inline-flex items-center gap-1 text-[10px] ${countTone.text}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {hasActionable ? (
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                    ) : null}
                    {hasCompleted ? (
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    ) : null}
                  </span>
                  <span>{count}</span>
                </span>
              ) : null}
              {count > 0 ? (
                <div className="absolute bottom-[110%] left-1/2 z-30 hidden w-64 -translate-x-1/2 rounded-lg border border-slate-200 bg-white p-2 text-left shadow-lg group-hover:block group-focus-within:block">
                  <div className="mb-1 text-[11px] text-slate-700">
                    <div className="font-semibold">Détail du jour</div>
                    <div className="flex flex-wrap items-center gap-1.5 text-slate-500">
                      {hasActionable ? (
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${getCalendarEventStateStyles("action").badge}`}
                        >
                          À faire: {actionableCount}
                        </span>
                      ) : null}
                      {hasCompleted ? (
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${getCalendarEventStateStyles("done").badge}`}
                        >
                          OK: {completedCount}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 text-slate-500">
                      {typeSummaryLabel || `${count} élément(s)`}
                    </div>
                  </div>
                  <div className="max-h-52 space-y-1 overflow-y-auto pr-1">
                    {details.map((detail, idx) => {
                      const stateTone = getCalendarEventStateStyles(
                        getCalendarEventState(detail.label),
                      );
                      return (
                        <button
                          key={`${detail.label}-${detail.targetId || "na"}-${idx}`}
                          type="button"
                          className="flex w-full items-center justify-between rounded px-1 py-0.5 text-[11px] text-slate-600 hover:bg-slate-100"
                          onClick={() => onCalendarEventClick?.(detail)}
                        >
                          <span className="flex min-w-0 items-center gap-2 pr-2 text-left">
                            <span
                              className={`h-2 w-2 shrink-0 rounded-full ${stateTone.dot}`}
                            />
                            <span className="truncate">{detail.label}</span>
                          </span>
                          <span className="font-medium text-slate-800 whitespace-nowrap">
                            x{detail.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function FundingTrackingIntroPanel({
  title,
  badge,
  subtitle,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  onReset,
  resultsCount,
  stats,
  activeStatKey,
  onSelectStat,
  actionLabel,
  onActionClick,
  calendarEvents = [],
  onCalendarEventClick,
  reminderItems = [],
  onReminderClick,
  statsLoading = false,
  loadingStatKeys = [],
}: {
  title: string;
  badge?: string | null;
  subtitle: string;
  searchPlaceholder: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onReset: () => void;
  resultsCount: number;
  stats: IntroStat[];
  activeStatKey?: string;
  onSelectStat?: (key: string) => void;
  actionLabel?: string;
  onActionClick?: () => void;
  calendarEvents?: CalendarEventInput[];
  onCalendarEventClick?: (item: CalendarDetailItem) => void;
  reminderItems?: FundingReminderItem[];
  onReminderClick?: (item: FundingReminderItem) => void;
  statsLoading?: boolean;
  loadingStatKeys?: string[];
}) {
  const loadingStatKeySet = useMemo(
    () => new Set((loadingStatKeys || []).map((key) => String(key))),
    [loadingStatKeys],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold text-slate-900">{title}</h2>
            {badge ? (
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm font-semibold text-slate-700">
                {badge}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        </div>

        {actionLabel && onActionClick ? (
          <Button type="button" onClick={onActionClick}>
            {actionLabel}
          </Button>
        ) : null}
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_290px]">
        <div className="rounded-3xl border border-slate-200 bg-white p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-9"
              />
            </div>
            <Button type="button" variant="ghost" onClick={onReset}>
              <X className="mr-1 h-4 w-4" />
              Réinitialiser
            </Button>
            <div className="text-sm font-semibold text-slate-700">
              {resultsCount} résultat(s)
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat) => {
              const isStatLoading = statsLoading || loadingStatKeySet.has(stat.key);
              const isInteractive = typeof onSelectStat === "function";
              const isActive = isInteractive && activeStatKey === stat.key;
              const tone = getToneStyles(stat.tone || "slate");
              const valueDisplay =
                stat.valueDisplay ??
                (typeof stat.value === "number"
                  ? stat.value.toLocaleString("fr-FR")
                  : stat.value);
              const cardClasses = [
                "rounded-2xl border p-3 text-left transition",
                tone.card,
                isInteractive
                  ? isActive
                    ? tone.activeRing
                    : tone.hoverRing
                  : "",
                isStatLoading ? "cursor-wait" : "",
              ]
                .filter(Boolean)
                .join(" ");

              if (!isInteractive) {
                return (
                  <div key={stat.key} className={cardClasses}>
                    <div className="flex items-start justify-between gap-3">
                      <div className={`text-sm font-semibold ${tone.label}`}>
                        {stat.label}
                      </div>
                      {!stat.hideDots ? (
                        <div className="mt-1 flex shrink-0 items-center gap-1">
                          {tone.dots.map((dotClass, index) => (
                            <span
                              key={`${stat.key}-dot-${index}`}
                              className={`h-2.5 w-2.5 rounded-full ${dotClass}`}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className={`mt-2 text-3xl font-semibold ${tone.value}`}>
                      {isStatLoading ? (
                        <span className="inline-block h-8 w-16 animate-pulse rounded-md bg-slate-300/60" />
                      ) : (
                        valueDisplay
                      )}
                    </div>
                    <div className={`text-sm ${tone.meta}`}>
                      {isStatLoading ? (
                        <span className="inline-block h-4 w-20 animate-pulse rounded bg-slate-300/50" />
                      ) : (
                        stat.meta || stat.suffix || "éléments"
                      )}
                    </div>
                  </div>
                );
              }

              return (
                <button
                  key={stat.key}
                  type="button"
                  onClick={() => onSelectStat(stat.key)}
                  disabled={isStatLoading}
                  className={cardClasses}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className={`text-sm font-semibold ${tone.label}`}>
                      {stat.label}
                    </div>
                    {!stat.hideDots ? (
                      <div className="mt-1 flex shrink-0 items-center gap-1">
                        {tone.dots.map((dotClass, index) => (
                          <span
                            key={`${stat.key}-dot-${index}`}
                            className={`h-2.5 w-2.5 rounded-full ${dotClass}`}
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className={`mt-2 text-3xl font-semibold ${tone.value}`}>
                    {isStatLoading ? (
                      <span className="inline-block h-8 w-16 animate-pulse rounded-md bg-slate-300/60" />
                    ) : (
                      valueDisplay
                    )}
                  </div>
                  <div className={`text-sm ${tone.meta}`}>
                    {isStatLoading ? (
                      <span className="inline-block h-4 w-20 animate-pulse rounded bg-slate-300/50" />
                    ) : (
                      stat.meta || stat.suffix || "éléments"
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <MiniCalendar
            events={calendarEvents}
            onCalendarEventClick={onCalendarEventClick}
          />
          <ReminderPanel
            reminderItems={reminderItems}
            onReminderClick={onReminderClick}
          />
        </div>
      </div>
    </div>
  );
}
